import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, User, Phone, Mail, ArrowRight, RefreshCw, Tag, Check } from 'lucide-react';
import { authService } from '../../services/authService';
import { authError } from '../../utils/authError';
import { persistSessionNative } from '../../utils/nativeSessionStore';
import Terms from '../legal/Terms';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const codeClean = (s: string) => s.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'details' | 'otp'>('details');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [agentCode, setAgentCode] = useState('');
  const [agentCheck, setAgentCheck] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');
  const [agentName, setAgentName] = useState('');

  // Confirm the agent's name as the code is typed, so a wrong code is caught here.
  useEffect(() => {
    if (!agentCode) { setAgentCheck('idle'); setAgentName(''); return; }
    setAgentCheck('checking');
    const timer = setTimeout(async () => {
      const res = await authService.checkAgentCode(agentCode);
      setAgentCheck(res.valid ? 'valid' : 'invalid');
      setAgentName(res.agentName || '');
    }, 400);
    return () => clearTimeout(timer);
  }, [agentCode]);

  const canSubmit = !!fullName && phone.length >= 9 && EMAIL_RE.test(email) && agreed
    && agentCheck !== 'invalid' && agentCheck !== 'checking';

  // Auto-detect OTP from SMS
  useEffect(() => {
    if ('OTPCredential' in window && step === 'otp') {
      const ac = new AbortController();
      (navigator as any).credentials.get({
        otp: { transport: ['sms'] },
        signal: ac.signal,
      }).then((otp: any) => {
        if (otp && otp.code) {
          setOtp(otp.code);
          // Auto-verify after a short delay
          setTimeout(() => {
            if (otp.code.length === 6) {
              // Trigger verify
              const event = new Event('submit');
              document.dispatchEvent(event);
            }
          }, 500);
        }
      }).catch(() => {});
      return () => ac.abort();
    }
  }, [step]);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const handleCreateAccount = async () => {
    if (!fullName || phone.length < 9) return;
    if (!EMAIL_RE.test(email)) { setError('Please enter a valid email address'); return; }
    if (!agreed) { setError('Please accept the Terms & Conditions to continue.'); return; }
    setIsLoading(true);
    setError('');
    try {
      // FIXED: Call signUp which creates user AND sends OTP
      const data = await authService.signUp(phone, fullName, email, agentCode || undefined);
      setSessionId(data.sessionId);
      setStep('otp');
      setCountdown(30);
    } catch (err: any) {
      setError(authError(err, 'Could not create your account. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-verify when OTP is 6 digits
  useEffect(() => {
    if (otp.length === 6 && step === 'otp') {
      handleVerifyOTP();
    }
  }, [otp]);

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await authService.verifyOTP(phone, otp, sessionId);
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      if (data.supabaseToken) localStorage.setItem('supabaseToken', data.supabaseToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('loginAt', String(Date.now()));
      localStorage.removeItem('sessionEndedReason');
      persistSessionNative();
      navigate('/home');
    } catch (err: any) {
      setError(authError(err, 'Invalid verification code.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    setOtp('');
    setError('');
    try {
      const data = await authService.requestOTP(phone);
      setSessionId(data.sessionId);
      setCountdown(30);
    } catch (err: any) {
      setError(authError(err, 'Could not resend the code.'));
    }
  };

  return (
    <div className="fixed inset-0 overflow-hidden bg-safaricab-darker flex flex-col">
      <div className="px-4 pt-[calc(max(var(--sai-top),14px)+24px)]">
        <button onClick={() => step === 'otp' ? setStep('details') : navigate('/')} className="text-white/50 hover:text-white flex items-center gap-2">
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">{step === 'otp' ? 'Change details' : 'Back'}</span>
        </button>
      </div>
      <div className="px-4 pt-6 mb-6 flex flex-col items-center text-center">
        <img src="/logo-lion.png" alt="SafariCab" className="w-28 h-28 object-contain mb-2" />
        <h3 className="text-white font-bold text-base tracking-wider">SAFARICAB</h3>
        <p className="text-safaricab-gold text-[10px] tracking-widest">ONLINE TAXI SERVICE</p>
      </div>
      <div className="px-4 flex-1 overflow-y-auto">
        {step === 'details' ? (
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} className="space-y-4 pb-8">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white mb-1">Create Account</h1>
              <p className="text-white/50 text-sm">Join SafariCab and start riding</p>
            </div>
            <div className="space-y-2">
              <label className="text-white/60 text-xs font-medium uppercase tracking-wider">Full Name</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="John Doe" className="w-full bg-safaricab-card border border-white/10 rounded-xl py-3.5 pl-12 pr-4 text-white placeholder-white/20 outline-none focus:border-safaricab-gold/50" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-white/60 text-xs font-medium uppercase tracking-wider">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^0-9+]/g, ''))} placeholder="+254712345678" className="w-full bg-safaricab-card border border-white/10 rounded-xl py-3.5 pl-12 pr-4 text-white text-lg placeholder-white/20 outline-none focus:border-safaricab-gold/50" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-white/60 text-xs font-medium uppercase tracking-wider">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="john@example.com" className="w-full bg-safaricab-card border border-white/10 rounded-xl py-3.5 pl-12 pr-4 text-white placeholder-white/20 outline-none focus:border-safaricab-gold/50" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-white/60 text-xs font-medium uppercase tracking-wider">Agent code (optional)</label>
              <div className="relative">
                <Tag className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type="text"
                  value={agentCode}
                  onChange={(e) => setAgentCode(codeClean(e.target.value).slice(0, 12))}
                  placeholder="SCM4KJ7P"
                  autoCapitalize="characters"
                  className={`w-full bg-safaricab-card border rounded-xl py-3.5 pl-12 pr-4 text-white font-mono tracking-wider placeholder-white/20 outline-none ${
                    agentCheck === 'invalid' ? 'border-red-400/60'
                      : agentCheck === 'valid' ? 'border-green-400/60'
                      : 'border-white/10 focus:border-safaricab-gold/50'
                  }`}
                />
              </div>
              {agentCheck === 'idle' && <p className="text-white/30 text-xs">If a SafariCab agent signed you up, enter their code</p>}
              {agentCheck === 'checking' && <p className="text-white/40 text-xs">Checking…</p>}
              {agentCheck === 'valid' && <p className="text-green-400 text-xs flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Referred by {agentName}</p>}
              {agentCheck === 'invalid' && <p className="text-red-400 text-xs">Code not recognised</p>}
            </div>

            <button type="button" onClick={() => { setAgreed(v => !v); setError(''); }} className="w-full flex items-start gap-3 text-left py-1">
              <span className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${agreed ? 'bg-safaricab-gold border-safaricab-gold' : 'border-white/30'}`}>
                {agreed && <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />}
              </span>
              <span className="text-white/60 text-sm leading-snug">
                I have read and agree to the{' '}
                <span role="link" tabIndex={0} onClick={(e) => { e.stopPropagation(); setShowTerms(true); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setShowTerms(true); } }} className="text-safaricab-gold underline underline-offset-2">
                  Terms &amp; Conditions
                </span>
              </span>
            </button>

            {error && <p className="text-red-400 text-sm text-center bg-red-400/5 py-3 rounded-xl">{error}</p>}
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleCreateAccount} disabled={isLoading || !canSubmit} className="w-full bg-safaricab-gold hover:bg-yellow-500 text-black font-bold py-4 rounded-xl flex items-center justify-center gap-3 disabled:opacity-40">
              {isLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <><span>Create Account</span><ArrowRight className="w-5 h-5" /></>}
            </motion.button>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white mb-1">Verify Phone</h1>
              <p className="text-white/50 text-sm">Enter the code sent to <span className="text-safaricab-gold">{phone}</span></p>
              <p className="text-white/30 text-xs mt-1">Check your SMS or email for the code</p>
            </div>
            <div className="space-y-2">
              <label className="text-white/60 text-xs font-medium uppercase tracking-wider">Verification Code</label>
              <input type="text" inputMode="numeric" value={otp} onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }} placeholder="000000" maxLength={6} className="w-full bg-safaricab-card border border-white/10 rounded-xl py-4 px-4 text-white text-center text-2xl tracking-[0.7em] placeholder-white/10 outline-none focus:border-safaricab-gold/50 font-mono" />
            </div>
            {error && <p className="text-red-400 text-sm text-center bg-red-400/5 py-3 rounded-xl">{error}</p>}
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleVerifyOTP} disabled={isLoading || otp.length !== 6} className="w-full bg-safaricab-gold hover:bg-yellow-500 text-black font-bold py-4 rounded-xl flex items-center justify-center gap-3 disabled:opacity-40">
              {isLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <><span>Verify & Continue</span><ArrowRight className="w-5 h-5" /></>}
            </motion.button>
            <button onClick={handleResendOTP} disabled={countdown > 0} className="w-full text-center text-sm text-white/40 hover:text-safaricab-gold disabled:text-white/20">
              {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend Code'}
            </button>
          </motion.div>
        )}
      </div>
      {/* Overlay rather than a route change: navigating to /terms and back would
          discard the name/phone/email already typed on this screen. */}
      {showTerms && <Terms onClose={() => setShowTerms(false)} />}
    </div>
  );
};

export default SignUp;
